import {
  ButtonActionName,
  createPersonaliseGAEvent,
  createProductItemGAEvent,
  ProductItemEventEnum,
  useBasket,
  useDigitalGiftingCardOptions,
  useHandleAddToBasket,
  Variant,
} from '@moonpig/web-shared-products'
import { trackGAEvent } from '@moonpig/web-core-analytics'
import { setUIHCookie } from '@moonpig/web-shared-utils'
import { getBrowserCookie } from '@moonpig/web-core-browser-storage'
import { useEffect } from 'react'
import { useAllEppoExperiments } from '@moonpig/web-core-eppo/browser'
import { logger } from '@moonpig/web-core-monitoring'
import { SelectedOptions, useProductContext } from '../../../contexts/product'
import { useFindLocaleText } from '../../../text-localisation'
import { ProductWithRecommendations } from '../../../components/ProductDetails'

const getSelectedVariant = ({
  product,
  variantKey,
}: {
  product: ProductWithRecommendations
  variantKey: string
}) => {
  return product.variants.find(
    // for digital gift cards we track the selected variant using variant sku not variant key
    // temporary if statement before refactoring to use variant sku across the board
    v => v.key === variantKey || v.sku === variantKey,
  )
}

const getSelectedAddon = ({
  selectedVariant,
  selectedOptions,
}: {
  selectedVariant: Variant
  selectedOptions: SelectedOptions
}) => {
  const { addonSku } = selectedOptions
  const addons = selectedVariant.addons || []
  return addons.find(addon => addon.sku === addonSku)
}

const getUIHCookieProductId = () => {
  const uihCookie = getBrowserCookie('UIH')
  const { moonpigProductNo } = JSON.parse(decodeURIComponent(uihCookie || '{}'))
  return moonpigProductNo
}

export const useModularAddToBasket = () => {
  const [{ product, selectedOptions }] = useProductContext()
  const [_, { setLoading, setError, setSelectedAmountWarning }] = useBasket()

  const experiments = useAllEppoExperiments()

  const localiseText = useFindLocaleText()

  const { getCanSeeCardOptions, seeCardOptionsForProduct } =
    useDigitalGiftingCardOptions()

  const soldOutMessage = localiseText('find.sold_out')
  const itsNotYouMessage = localiseText('find.its_not_you')

  const { canSeeCardOptions } = getCanSeeCardOptions(
    product.category.department,
  )

  const { handleAddToBasket, addingToBasket } = useHandleAddToBasket({
    pageLocation: 'product details',
    soldOutMessage,
    itsNotYouMessage,
  })

  const buttonActionName = product.customisable
    ? ButtonActionName.PERSONALISE
    : ButtonActionName.ADD_TO_BASKET

  const redirectToSeeCardOptions = (selectedVariant: Variant) => {
    const gaEvent = createProductItemGAEvent({
      eventType: ProductItemEventEnum.SELECT_ITEM,
      product,
      variant: selectedVariant.title,
      index: 0,
      listName: '',
      label: '',
    })

    seeCardOptionsForProduct({
      productId: product.id,
      gaEvent,
    })
    return Promise.resolve()
  }

  const defaultAddToBasketFlow = async (selectedVariant: Variant) => {
    await handleAddToBasket({
      quantity: selectedOptions.quantity,
      product,
      variant: selectedVariant,
      itemIndex: 0,
      productsLength: 1,
      buttonActionName,
      experiments,
      selectedAddon: getSelectedAddon({ selectedVariant, selectedOptions }),
      componentName: 'product details',
    })

    if (product.customisable) {
      trackGAEvent(
        createPersonaliseGAEvent({
          componentName: 'product details modular',
          productData: {
            id: product.id,
            name: product.title,
            price: selectedVariant.price.centAmount,
            variant: selectedVariant.title,
            quantity: 1,
            category: product.category.name,
          },
        }),
      )
    }
  }

  const addProductToBasket = async () => {
    const cookieProductId = getUIHCookieProductId()
    if (product.id !== cookieProductId) setUIHCookie({ product })

    const selectedVariant = getSelectedVariant({
      product,
      variantKey: selectedOptions.variantKey,
    }) as Variant

    if (!selectedVariant) {
      setSelectedAmountWarning(true)
      return
    }

    try {
      const addToBasketAction = canSeeCardOptions
        ? redirectToSeeCardOptions
        : defaultAddToBasketFlow

      await addToBasketAction(selectedVariant)
    } catch (error) {
      logger.fixToday('Failure adding product to basket', error)
      setError(true)
    }
  }

  useEffect(() => {
    setLoading(addingToBasket)
  }, [addingToBasket, setLoading])

  return {
    addProductToBasket,
    canSeeCardOptions,
  }
}
